import { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import beGetClientSmsText from 'actions/sms/beGetClientSmsText.action';
import determineHoursLaterForNcob from 'bl/ncob/determineHoursLaterForNcob.bl';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import withSalonId from 'components/hoc/withSalonId.hoc';
import WidgetTiming from 'components/widget/WidgetTiming.component';
import NCOB_OFFSET_HOURS from 'constants/settings/ncobOffsetHours.const';
import NCOB_TYPE_TIME from 'constants/settings/ncobTypeTime.const';
import useAfn from 'hooks/useAfn.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useNcobSettings } from 'providers/NcobSettings.provider';
import { useTheme } from 'providers/Theme.provider';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import CN from './TimingPanel.module.scss';

const L = logger('TimingPanel');

const NO_HOURS_OFFSET = '00:00:00';

const byDescription = ($) => (candidate) => asObject(candidate).description === $;

const TimingPanel = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const { value, isLoading, error, update, read } = useNcobSettings();
  const { ncobCancelWindow } = asObject(value);

  const CANCELATION_TIMING_DESCRIPTION = Object.freeze({
    [NCOB_TYPE_TIME.oneDay]: t('common.hours.24h'),
    [NCOB_TYPE_TIME.twoDays]: t('common.hours.48h'),
    [NCOB_TYPE_TIME.threeDays]: t('common.hours.72h'),
  });

  const [, handleSaveHours] = useAfn(
    async ({ raw }) => {
      if (raw === null) return { value: ncobCancelWindow };

      const offsetValue = NCOB_OFFSET_HOURS.find(byDescription(raw.description));
      if (!offsetValue) return { value: ncobCancelWindow };

      const { value } = await update({ data: { ncobCancelWindow: offsetValue.value } });
      return value;
    },
    [ncobCancelWindow, update],
  );

  const [, handleSaveTiming] = useAfn(
    async ({ raw }) => {
      if (raw === null) return { value: ncobCancelWindow };

      const { hour } = raw;

      const { ok, value } = await update({ data: { ncobCancelWindow: hour } });
      return ok ? value : { value: hour };
    },
    [ncobCancelWindow, update],
  );

  const select = useCallback((ev, selectedHour) => void update({ data: { ncobCancelWindow: selectedHour } }), [update]);

  const handleSelectNone = useCallback((ev) => select(ev, NCOB_TYPE_TIME.none), [select]);
  const handleSelectOneDay = useCallback((ev) => select(ev, NCOB_TYPE_TIME.oneDay), [select]);

  const hoursDescription = useMemo(() => {
    // NOTE: it will be undefined in case of NCOB_TYPE_TIME.none
    const description = CANCELATION_TIMING_DESCRIPTION[ncobCancelWindow];
    if (description) return description;

    const hoursValue = determineHoursLaterForNcob(ncobCancelWindow);
    const hoursUnit = hoursValue === 1 ? t('common.hours.hour') : t('common.hours.hours');
    return `${hoursValue} ${hoursUnit}`;
  }, [CANCELATION_TIMING_DESCRIPTION, ncobCancelWindow, t]);

  const isCancelingAllowed = ncobCancelWindow !== NO_HOURS_OFFSET;

  if (error) L.warn('()', { isLoading, ncobCancelWindow, error, value, read });

  L.debug('()', { ncobCancelWindow });

  return (
    <div
      data-bem="TimingPanel"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      {isLoading && <OverlaySpinner />}
      <div data-bem="TimingPanel__title" className={CN.sectionTitle}>
        {t('onlineBooking.policyAndTerms.cancellationPolicy.title2')}
      </div>
      <div data-bem="TimingPanel__subtitle" className={CN.text}>
        {t('onlineBooking.policyAndTerms.cancellationPolicy.description2')}
      </div>
      <div
        data-bem="TimingPanel__option-not-allowed"
        className={cn({
          [CN.selectOption]: true,
          [CN.selected]: !isCancelingAllowed,
        })}
      >
        <div data-value={NCOB_TYPE_TIME.none} className={CN.iconContainer} role="button" onClick={handleSelectNone} tabIndex={0}>
          <Icon tag={NCOB_TYPE_TIME.none} variant={isCancelingAllowed ? 'markCircleUnselected' : 'markCircle'} />
        </div>

        <span className={CN.selectionText}>{t('onlineBooking.policyAndTerms.cancellationPolicy.option1')}</span>
      </div>
      <div
        data-bem="TimingPanel__option-allowed"
        className={cn({
          [CN.selectOption]: true,
          [CN.selected]: isCancelingAllowed,
        })}
      >
        <div data-value={NCOB_TYPE_TIME.oneDay} className={CN.iconContainer} role="button" onClick={handleSelectOneDay} tabIndex={0}>
          <Icon tag={NCOB_TYPE_TIME.oneDay} variant={isCancelingAllowed ? 'markCircle' : 'markCircleUnselected'} />
        </div>

        <span className={CN.selectionText}>{t('onlineBooking.policyAndTerms.cancellationPolicy.option2', { hours: hoursDescription })}</span>
      </div>
      <div data-bem="TimingPanel__timings" className={CN.timingContainer}>
        {isCancelingAllowed && (
          <div className={CN.timing}>
            <div className={CN.timingButtons}>
              <WidgetTiming name="repeat" onChangeTiming={handleSaveTiming} onChangeHours={handleSaveHours} tag="TimingPanel_timing" value={ncobCancelWindow} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => bindActionCreators({ beGetClientSmsText }, dispatch);

const TimingPanelHoc = connect(null, mapDispatchToProps)(withSalonId(TimingPanel));

export default TimingPanelHoc;
